import React, { useState, useMemo } from 'react';
import './style.css';
import LayoutHome from '../../components/layout/LayoutHome';
import {
  Input, Button, Table, Tag, Typography, Row, Col, Modal, Form,
  InputNumber, Space, message, Divider, Grid, Card, List, Select, DatePicker, Radio, ConfigProvider
} from 'antd';
import {
  SearchOutlined, PlusOutlined, WalletOutlined, BankOutlined, CopyOutlined,
  ShopOutlined, CalendarOutlined, TagsOutlined, EditOutlined, DeleteOutlined, DollarOutlined, DownloadOutlined
} from '@ant-design/icons';
import { useGetGastos, useSaveGasto, useDeleteGasto, useGetCategoriasGastos, useSaveCategoriaGasto } from '../../hooks/useGastos';
import { useCaja } from '../../hooks/useCaja';
import es_ES from 'antd/locale/es_ES';
import dayjs from 'dayjs';
// @ts-ignore
import 'dayjs/locale/es';
import { usePermission } from '../../hooks/usePermission';
dayjs.locale('es');

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const COLORES_PREDEFINIDOS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981',
  '#06b6d4', '#3b82f6', '#6366f1', '#a855f7', '#ec4899', '#64748b'
];

const Gastos = () => {

  const { hasPermission } = usePermission();

  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const { getCajaActiva } = useCaja();
  const cajaActiva = getCajaActiva?.data;

  const { data: categorias = [] } = useGetCategoriasGastos();
  const [busqueda, setBusqueda] = useState('');
  const [fechas, setFechas] = useState([dayjs().startOf('month'), dayjs().endOf('day')]);

  const { data: gastosDB = [], isLoading } = useGetGastos({
    desde: fechas[0].format('YYYY-MM-DD'),
    hasta: fechas[1].format('YYYY-MM-DD')
  });

  const saveGasto = useSaveGasto();
  const deleteGasto = useDeleteGasto();
  const saveCategoria = useSaveCategoriaGasto();

  const [editingCat, setEditingCat] = useState(null);
  const [editingGasto, setEditingGasto] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalCatOpen, setIsModalCatOpen] = useState(false);

  const [form] = Form.useForm();
  const [formCat] = Form.useForm();
  const watchColor = Form.useWatch('color', formCat);
  const [isModalExportOpen, setIsModalExportOpen] = useState(false);
  const [tipoExportacion, setTipoExportacion] = useState('filtrados');

  const gastosFiltrados = useMemo(() => {
    if (!Array.isArray(gastosDB)) return [];
    return gastosDB.filter(g =>
      g.descripcion?.toLowerCase().includes(busqueda.toLowerCase()) ||
      g.categoria_nombre?.toLowerCase().includes(busqueda.toLowerCase())
    );
  }, [gastosDB, busqueda]);

  const stats = useMemo(() => {
    const total = gastosFiltrados.reduce((acc, g) => acc + Number(g.monto), 0);
    const caja = gastosFiltrados.filter(g => g.metodo_pago === 'Efectivo Caja').reduce((acc, g) => acc + Number(g.monto), 0);
    return { total, caja, bancos: total - caja };
  }, [gastosFiltrados]);

  const handleEditGasto = (gasto) => {
    setEditingGasto(gasto);
    form.setFieldsValue({
      monto: gasto.monto,
      categoria_id: gasto.categoria_id,
      metodo_pago: gasto.metodo_pago,
      descripcion: gasto.descripcion
    });
    setIsModalOpen(true);
  };

  const handleDeleteGasto = (gasto) => {
    Modal.confirm({
      title: '¿Estás seguro de eliminar este gasto?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Sí, eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: () => {
        deleteGasto.mutate(gasto.id, {
          onSuccess: () => message.success("Gasto eliminado exitosamente"),
          onError: (err) => message.error(err.message)
        });
      }
    });
  };

  const onFinishGasto = (values) => {
    const fechaSeleccionada = values.fecha ? dayjs(values.fecha) : dayjs();

    if (fechaSeleccionada.isAfter(dayjs(), 'day')) {
      return message.error("No puedes registrar un gasto con una fecha posterior a la de hoy.");
    }
    
    if (values.metodo_pago === 'Efectivo Caja') {
      if (!cajaActiva) {
        return message.error("No puedes registrar un gasto en efectivo sin una caja abierta.");
      }
      if (values.fecha && dayjs(values.fecha).isBefore(dayjs(cajaActiva.fecha_apertura), 'day')) {
        return message.error("La fecha del gasto no puede ser anterior a la apertura de la caja actual.");
      }
    }

    const fechaFinal = fechaSeleccionada
      .hour(dayjs().hour())
      .minute(dayjs().minute())
      .second(dayjs().second())
      .toISOString();;

    const payload = {
      id: editingGasto?.id,
      categoria_id: values.categoria_id,
      monto: values.monto,
      metodo_pago: values.metodo_pago,
      descripcion: values.descripcion,
      fecha: fechaFinal,
      sesion_caja_id: values.metodo_pago === 'Efectivo Caja' ? cajaActiva?.id : null
    };

    saveGasto.mutate(payload, {
      onSuccess: () => {
        message.success(editingGasto ? "Gasto actualizado" : "Gasto registrado exitosamente");
        handleCloseModalGasto();
      },
      onError: (err) => message.error("Error Gasto: " + (err.message || "Error desconocido"))
    });
  };

  const handleCloseModalGasto = () => {
    setIsModalOpen(false);
    setEditingGasto(null);
    form.resetFields();
  };

  const onFinishCategoria = (values) => {
    const colorHex = typeof values.color === 'string' ? values.color : values.color?.toHexString();
    const payload = { nombre: values.nombre, color_etiqueta: colorHex };

    if (editingCat) {
      payload.id = editingCat.id;
    }

    saveCategoria.mutate(payload, {
      onSuccess: () => {
        message.success(editingCat ? "Categoría actualizada" : "Categoría creada");
        setIsModalCatOpen(false);
        setEditingCat(null);
        formCat.resetFields();
      },
      onError: (err) => message.error(err.message)
    });
  };

  const renderMetodoPago = (metodo) => {
    let icon, color;
    if (metodo === 'Efectivo Caja') {
      icon = <WalletOutlined />; color = '#faad14';
    } else if (metodo === 'Efectivo Externo') {
      icon = <DollarOutlined />; color = '#52c41a';
    } else {
      icon = <BankOutlined />; color = '#22BABB';
    }
    return (
      <Space>
        {React.cloneElement(icon, { style: { color } })}
        <Text size="small">{metodo}</Text>
      </Space>
    );
  };

  const aplicarFiltroRapido = (e) => {
    const tipo = e.target.value;
    const hoy = dayjs();

    if (tipo === 'hoy') {
      setFechas([hoy.startOf('day'), hoy.endOf('day')]);
    } else if (tipo === 'semana') {
      setFechas([hoy.startOf('week'), hoy.endOf('day')]);
    } else if (tipo === 'mes') {
      setFechas([hoy.startOf('month'), hoy.endOf('day')]);
    }
  };

  const handleDuplicarGasto = (gasto) => {
    setEditingGasto(null);
    form.setFieldsValue({
      fecha: dayjs(),
      monto: gasto.monto,
      categoria_id: gasto.categoria_id,
      metodo_pago: gasto.metodo_pago,
      descripcion: gasto.descripcion || ''
    });
    setIsModalOpen(true);
  };

  const columnasWeb = [
    {
      title: 'FECHA Y HORA',
      dataIndex: 'fecha',
      width: 180,
      render: (f) => dayjs(f).format('DD/MM/YYYY hh:mm A')
    },
    {
      title: 'CATEGORÍA',
      width: 200,
      dataIndex: 'categoria_nombre',
      render: (cat, r) => <Tag color={r.color_etiqueta || 'blue'} style={{ borderRadius: '4px' }}>{cat?.toUpperCase()}</Tag>
    },
    {
      title: 'MONTO DE DINERO',
      width: 180,
      dataIndex: 'monto',
      render: (v) => <Text strong style={{ color: '#1F1F1F' }}>$ {Number(v).toLocaleString('es-CO')}</Text>
    },
    {
      title: 'MÉTODO DE PAGO',
      width: 180,
      dataIndex: 'metodo_pago',
      render: (m) => renderMetodoPago(m)
    },
    {
      title: 'DESCRIPCIÓN',
      dataIndex: 'descripcion',
      render: (d) => <Text type="secondary">{d}</Text>
    },
    {
      title: 'ACCIONES',
      key: 'acciones',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            style={{ color: '#64748b' }}
            icon={<CopyOutlined />}
            onClick={() => handleDuplicarGasto(record)}
            disabled={!hasPermission('gastos:crear')}
            title="Duplicar este gasto"
          />
          <Button
            type="text"
            style={{ color: '#22BABB' }}
            icon={<EditOutlined />}
            onClick={() => handleEditGasto(record)}
            disabled={!hasPermission('gastos:editar') ? true : false}
            title={!hasPermission('gastos:editar') ? 'No tienes permiso para esta acción' : 'Editar'}
          />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteGasto(record)}
            disabled={!hasPermission('gastos:eliminar') ? true : false}
            title={!hasPermission('gastos:eliminar') ? 'No tienes permiso para esta acción' : 'Eliminar'}
          />
        </Space>
      )
    }
  ];

  const handleDescargarExportacion = () => {

    const dataAExportar = tipoExportacion === 'todos' ? gastosDB : gastosFiltrados;

    if (dataAExportar.length === 0) {
      return message.warning("No hay datos en el rango seleccionado para exportar.");
    }

    const headers = ["Fecha y Hora", "Categoría", "Monto", "Método de Pago", "Descripción\n"];

    const rows = dataAExportar.map(g => [
      `"${dayjs(g.fecha).format('DD/MM/YYYY hh:mm A')}"`,
      `"${g.categoria_nombre?.toUpperCase() || ''}"`,
      `"${g.monto}"`,
      `"${g.metodo_pago || ''}"`,
      `"${g.descripcion?.replace(/"/g, '""') || ''}"\n`
    ]);

    const csvContent = "\uFEFF" + headers.join(",") + rows.map(e => e.join(",")).join("");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);

    const fDesde = fechas[0].format('YYYY-MM-DD');
    const fHasta = fechas[1].format('YYYY-MM-DD');
    link.setAttribute("download", `Reporte_Gastos_desde_${fDesde}_hasta_${fHasta}.csv`);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success("Reporte descargado exitosamente");
    setIsModalExportOpen(false);
  };

  return (
    <ConfigProvider locale={es_ES}>
      <LayoutHome pageSeleccionada="gastos">
        <div className='containerGastos'>

          {/* HEADER & KPIs */}
          <div className='headerKapis'>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '15px' }}>
              {!isMobile && <Title level={6} style={{ margin: 0 }}>Gestión de Gastos</Title>}
              <div style={{ background: '#f8fafc', padding: isMobile ? '12px' : '12px 20px', borderRadius: '16px', border: '1px solid #e2e8f0', width: isMobile ? '100%' : 'auto' }}>
                <div style={{ display: 'flex', gap: isMobile ? '8px' : '25px', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'wrap' }}>
                  <Space direction="vertical" size={0} align="center">
                    <Text type="secondary" style={{ fontSize: '10px' }}>TOTAL EGRESOS</Text>
                    <Text strong style={{ fontSize: isMobile ? '14px' : '18px', color: '#ef4444' }}>$ {stats.total.toLocaleString('es-CO')}</Text>
                  </Space>
                  <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                  <Space direction="vertical" size={0} align="center">
                    <Text type="secondary" style={{ fontSize: '10px' }}>DESDE CAJA</Text>
                    <Text strong style={{ fontSize: isMobile ? '14px' : '18px' }}>$ {stats.caja.toLocaleString('es-CO')}</Text>
                  </Space>
                  <Divider type="vertical" style={{ height: '24px', margin: 0 }} />
                  <Space direction="vertical" size={0} align="center">
                    <Text type="secondary" style={{ fontSize: '10px' }}>BANCOS / OTROS</Text>
                    <Text strong style={{ fontSize: isMobile ? '14px' : '18px', color: '#22BABB' }}>$ {stats.bancos.toLocaleString('es-CO')}</Text>
                  </Space>
                </div>
              </div>
            </div>
          </div>

          {/* FILTROS Y BOTONES SUPERIORES */}
          <Row className='filtrosGastos' gutter={[8, 8]} align="bottom">

            <Col xs={24} md={6}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>{ isMobile ? '' : 'Buscar por categoría o descripción' }</Text>
                <Input
                  placeholder="Ej: Arriendo, internet, transporte..."
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  style={{ height: isMobile ? '40px' : '45px', borderRadius: '12px' }}
                  value={busqueda}
                  allowClear
                  onChange={e => setBusqueda(e.target.value)}
                />
              </Space>
            </Col>

            <Col xs={24} md={5}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>{ isMobile ? '' : 'Filtros por día, semana o mes'}</Text>
                <Radio.Group
                  defaultValue="mes"
                  onChange={aplicarFiltroRapido}
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Radio.Button value="hoy" style={{ flex: 1, textAlign: 'center', height: isMobile ? '40px' : '45px', lineHeight: isMobile ? '40px' : '45px' }}>Hoy</Radio.Button>
                  <Radio.Button value="semana" style={{ flex: 1, textAlign: 'center', height: isMobile ? '40px' : '45px', lineHeight: isMobile ? '40px' : '45px' }}>Semana</Radio.Button>
                  <Radio.Button value="mes" style={{ flex: 1, textAlign: 'center', height: isMobile ? '40px' : '45px', lineHeight: isMobile ? '40px' : '45px' }}>Mes</Radio.Button>
                </Radio.Group>
              </Space>
            </Col>

            <Col xs={12} md={3}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Desde</Text>
                <DatePicker
                  placeholder="Seleccione fecha"
                  style={{ width: '100%', height: isMobile ? '40px' : '45px', borderRadius: '12px' }}
                  value={fechas[0]}
                  onChange={(val) => setFechas([val || dayjs(), fechas[1]])}
                  disabledDate={(current) => current && current > fechas[1]}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </Space>
            </Col>

            <Col xs={12} md={3}>
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '500' }}>Hasta</Text>
                <DatePicker
                  placeholder="Seleccione fecha"
                  style={{ width: '100%', height: isMobile ? '40px' : '45px', borderRadius: '12px' }}
                  value={fechas[1]}
                  onChange={(val) => setFechas([fechas[0], val || dayjs()])}
                  disabledDate={(current) => current && current < fechas[0].startOf('day')}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </Space>
            </Col>

            <Col xs={8} md={1}>
              <Button
                block
                icon={<DownloadOutlined />}
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '12px',
                  color: '#475569',
                  borderColor: '#cbd5e1'
                }}
                onClick={() => setIsModalExportOpen(true)}
                title="Exportar a Excel / CSV"
              >
                {!isMobile && ""}
              </Button>
            </Col>

            <Col xs={8} md={3}>
              <Button
                block icon={<TagsOutlined />}
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '12px',
                  color: '#22BABB',
                  borderColor: '#22BABB',
                  fontSize: isMobile ? '12px' : '13px',
                  padding: '4px 2px'
                }}
                onClick={() => setIsModalCatOpen(true)}
                disabled={!hasPermission('gastos:crear_editar_categoria')}
                title="Categorías"
              >
                CATEGORÍAS
              </Button>
            </Col>

            {/* 7. BOTÓN REGISTRAR */}
            <Col xs={8} md={3}>
              <Button
                type="primary" block icon={<PlusOutlined />}
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '12px',
                  background: '#22BABB',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '12px' : '13px',
                  padding: '4px 2px'
                }}
                onClick={() => {
                  setEditingGasto(null);
                  setIsModalOpen(true);
                  form.setFieldsValue({ fecha: dayjs(), metodo_pago: undefined });
                }}
                disabled={!hasPermission('gastos:crear')}
                title="Registrar Gasto"
              >
                NUEVO
              </Button>
            </Col>
          </Row>

          {/* TABLA / LISTA */}
          <div className='tablaContenedor'>
            {isMobile ? (
              <List
                className="listaMovil"
                dataSource={gastosFiltrados}
                renderItem={g => (
                  <Card style={{ marginBottom: '12px', borderRadius: '16px' }} styles={{ body: { padding: '16px' } }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <Tag color={g.color_etiqueta || 'blue'}>{g.categoria_nombre}</Tag>
                        <div style={{ marginTop: '8px' }}><Text strong>{g.descripcion}</Text></div>
                      </div>
                      <Text strong style={{ color: '#ef4444' }}>$ {Number(g.monto).toLocaleString('es-CO')}</Text>
                    </div>
                    <Divider style={{ margin: '12px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}><CalendarOutlined /> {dayjs(g.fecha).format('DD/MM/YY hh:mm A')}</Text>
                      <Space>
                        {renderMetodoPago(g.metodo_pago)}
                      </Space>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                      <Space>
                        <Button
                          type="text"
                          size="small"
                          style={{ color: '#22BABB' }}
                          icon={<EditOutlined />}
                          onClick={() => handleEditGasto(g)}
                          disabled={!hasPermission('gastos:editar') ? true : false}
                          title={!hasPermission('gastos:editar') ? 'No tienes permiso para esta acción' : 'Editar'}
                        />
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteGasto(g)}
                          disabled={!hasPermission('gastos:eliminar') ? true : false}
                          title={!hasPermission('gastos:eliminar') ? 'No tienes permiso para esta acción' : 'Eliminar'}
                        />
                      </Space>
                    </div>
                  </Card>
                )}
              />
            ) : (
              <Table
                columns={columnasWeb}
                dataSource={gastosFiltrados}
                rowKey="id"
                loading={isLoading}
                pagination={{ pageSize: 10 }}
                scroll={{ y: 'max-content' }}
              />
            )}
          </div>
        </div>

        {/* MODAL NUEVO/EDITAR GASTO */}
        <Modal
          title={null}
          open={isModalOpen}
          onCancel={handleCloseModalGasto}
          footer={null}
          centered
          width={480}
          styles={{
            mask: {
              background: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(5px)'
            }
          }}
        >
          <div style={{ textAlign: 'center', padding: '15px 0 25px' }}>
            <div style={{ background: '#fef2f2', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <ShopOutlined style={{ fontSize: '26px', color: '#ef4444' }} />
            </div>
            <Title level={4} style={{ margin: 0, fontSize: '20px' }}>{editingGasto ? 'Editar Gasto' : 'Registrar Gasto'}</Title>
            <Text type="secondary">Organiza tus salidas de dinero del negocio.</Text>
          </div>

          <Form form={form} layout="vertical" onFinish={onFinishGasto}>

            <Row gutter={12}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="fecha"
                  label={<Text strong style={{ fontSize: '13px' }}>Fecha del Gasto</Text>}
                  rules={[{ required: true, message: 'Seleccione la fecha' }]}
                >
                  <DatePicker
                    style={{ width: '100%', height: '45px', borderRadius: '8px' }}
                    size="large"
                    format="DD/MM/YYYY"
                    allowClear={false}
                    disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="monto" label={<Text strong style={{ fontSize: '13px' }}>Monto a pagar</Text>} rules={[{ required: true, message: 'Ingrese el monto' }]}>
                  <InputNumber
                    controls={false}
                    style={{ width: '100%', height: '45px', borderRadius: '8px' }}
                    size="large"
                    min={1}
                    formatter={v => `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={v => v.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="categoria_id" label={<Text strong style={{ fontSize: '13px' }}>Categoría del Gasto</Text>} rules={[{ required: true, message: 'Seleccione la categoría' }]}>
              <Select size="large" placeholder="Selecciona el tipo de gasto" style={{ height: '45px' }}>
                {categorias.map(c => <Select.Option key={c.id} value={c.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: c.color_etiqueta || '#22BABB' }}></div>
                    {c.nombre}
                  </div>
                </Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item
              name="metodo_pago"
              label={<Text strong style={{ fontSize: '13px' }}>¿Cómo se pagó?</Text>}
              rules={[
                { required: true, message: 'Seleccione el método de pago' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value === 'Efectivo Caja' && cajaActiva) {
                      const fechaSel = getFieldValue('fecha');
                      if (fechaSel && dayjs(fechaSel).isBefore(dayjs(cajaActiva.fecha_apertura), 'day')) {
                        return Promise.reject(new Error('La fecha no puede ser anterior a la apertura de la caja.'));
                      }
                    }
                    return Promise.resolve();
                  },
                })
              ]}
            >
              <Select size="large" placeholder="Seleccione el método de pago" style={{ height: '45px' }}>
                <Select.Option value="Efectivo Caja" disabled={!cajaActiva}>
                  <Tag color={cajaActiva ? "warning" : "default"}>CAJA</Tag>
                  {cajaActiva ? 'Sacar de caja actual' : 'Caja cerrada (Requiere apertura)'}
                </Select.Option>
                <Select.Option value="Efectivo Externo">
                  <Tag color="green">EFECTIVO</Tag> Dinero fuera de caja
                </Select.Option>
                <Select.Option value="Transferencia">
                  <Tag color="blue">BANCO</Tag> Cuenta / Transferencia
                </Select.Option>
              </Select>
            </Form.Item>

            <Form.Item name="descripcion" label={<Text strong style={{ fontSize: '13px' }}>Descripción / Detalle</Text>} rules={[{ required: true, message: 'La descripción es requerida' }]}>
              <Input.TextArea rows={2} style={{ borderRadius: '8px' }} placeholder="Ej: Pago de recibo de luz mes actual" />
            </Form.Item>

            <Button
              type="primary" htmlType="submit" block size="large"
              loading={saveGasto.isPending || saveGasto.isLoading}
              disabled={saveGasto.isPending || saveGasto.isLoading}
              style={{ background: '#22BABB', height: '50px', borderRadius: '12px', fontWeight: 'bold', marginTop: '10px', fontSize: '15px' }}
            >
              CONFIRMAR Y GUARDAR
            </Button>
          </Form>
        </Modal>

        {/* MODAL CREAR CATEGORÍA */}
        <Modal
          title={null}
          open={isModalCatOpen}
          onCancel={() => { setIsModalCatOpen(false); setEditingCat(null); formCat.resetFields(); }}
          footer={null}
          centered
          width={420}
          styles={{
            mask: {
              background: 'rgba(15, 23, 42, 0.75)',
              backdropFilter: 'blur(5px)'
            }
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ background: '#f0fdf4', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <TagsOutlined style={{ fontSize: '26px', color: '#22c55e' }} />
            </div>
            <Title level={4} style={{ margin: 0 }}>{editingCat ? 'Editar Categoría' : 'Gestionar Categorías'}</Title>
          </div>

          {!editingCat && (
            <div style={{
              maxHeight: '180px',
              overflowY: 'auto',
              marginBottom: '20px',
              border: '1px solid #f1f5f9',
              borderRadius: '12px',
              padding: '6px 12px',
              background: '#f8fafc'
            }}>
              <List
                size="small"
                dataSource={categorias}
                renderItem={item => (
                  <List.Item
                    style={{ padding: '8px 0' }}
                    actions={[
                      <Button type="link" style={{ color: '#22BABB', fontWeight: '500' }} size="small" onClick={() => {
                        setEditingCat(item);
                        formCat.setFieldsValue({ nombre: item.nombre, color: item.color_etiqueta || '#22BABB' });
                      }}>Editar</Button>
                    ]}
                  >
                    <Space>
                      <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: item.color_etiqueta || '#22BABB' }}></div>
                      <Text strong={false} style={{ color: '#334155' }}>{item.nombre}</Text>
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          )}

          <Form form={formCat} layout="vertical" onFinish={onFinishCategoria} initialValues={{ color: '#22BABB' }}>
            <Form.Item
              name="nombre"
              label={<Text strong style={{ fontSize: '13px' }}>Nombre de la Categoría</Text>}
              rules={[
                { required: true, message: 'El nombre es requerido' },
                () => ({
                  validator(_, value) {
                    if (!value) return Promise.resolve();

                    // Limpieza Extrema: quita tildes, mayúsculas, espacios múltiples y eñes
                    const normalizar = (txt) => txt.trim().toLowerCase()
                      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                      .replace(/\s+/g, ' ');

                    const textoFormulario = normalizar(value);

                    const existe = categorias.some(c => {
                      if (editingCat && c.id === editingCat.id) return false;
                      return normalizar(c.nombre) === textoFormulario;
                    });

                    if (existe) {
                      return Promise.reject(new Error('Ya existe una categoría con este mismo nombre (o muy similar).'));
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <Input size="large" style={{ height: '45px', borderRadius: '8px' }} placeholder="Ej: Servicios Públicos" allowClear />
            </Form.Item>

            <Form.Item name="color" label={<Text strong style={{ fontSize: '13px' }}>Selecciona un Color Identificador</Text>}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', padding: '8px 4px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', justifyContent: 'center' }}>
                {COLORES_PREDEFINIDOS.map(col => (
                  <div
                    key={col}
                    onClick={() => formCat.setFieldsValue({ color: col })}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: col,
                      cursor: 'pointer',
                      border: watchColor === col ? '3px solid #0f172a' : '2px solid #fff',
                      boxShadow: watchColor === col ? '0 0 10px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                      transform: watchColor === col ? 'scale(1.15)' : 'scale(1)',
                      transition: 'all 0.15s ease-in-out'
                    }}
                  />
                ))}
              </div>
            </Form.Item>

            <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: '20px' }} size="middle">
              {editingCat && (
                <Button
                  size="large"
                  onClick={() => { setEditingCat(null); formCat.resetFields(); }}
                  style={{ borderRadius: '8px', height: '45px' }}
                >
                  Cancelar
                </Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={saveCategoria.isPending || saveCategoria.isLoading}
                disabled={saveCategoria.isPending || saveCategoria.isLoading}
                style={{ background: '#0f172a', color: '#fff', height: '45px', borderRadius: '8px', fontWeight: 'bold', padding: '0 24px' }}
              >
                {editingCat ? 'Actualizar' : 'Crear Categoría'}
              </Button>
            </Space>
          </Form>
        </Modal>

        {/* MODAL DE EXPORTACIÓN */}
        <Modal
          title={null}
          open={isModalExportOpen}
          onCancel={() => setIsModalExportOpen(false)}
          footer={null}
          centered
          width={450}
          styles={{ mask: { background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)' } }}
        >
          <div style={{ textAlign: 'center', padding: '15px 0 20px' }}>
            <div style={{ background: '#f0f9ff', width: '55px', height: '55px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <DownloadOutlined style={{ fontSize: '26px', color: '#0284c7' }} />
            </div>
            <Title level={4} style={{ margin: 0, fontSize: '20px' }}>Exportar Gastos</Title>
            <Text type="secondary">Genera un archivo Excel/CSV de tus registros.</Text>
          </div>

          <div style={{ marginBottom: '20px', background: '#f8fafc', padding: '16px', borderRadius: '12px' }}>
            <Text strong>Confirma el rango de fechas:</Text>
            <Row gutter={8} style={{ marginTop: '10px' }}>
              <Col span={12}>
                <DatePicker
                  placeholder="Desde"
                  style={{ width: '100%', height: '40px', borderRadius: '8px' }}
                  value={fechas[0]}
                  onChange={(val) => setFechas([val || dayjs(), fechas[1]])}
                  disabledDate={(current) => current && current > fechas[1]}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </Col>
              <Col span={12}>
                <DatePicker
                  placeholder="Hasta"
                  style={{ width: '100%', height: '40px', borderRadius: '8px' }}
                  value={fechas[1]}
                  onChange={(val) => setFechas([fechas[0], val || dayjs()])}
                  disabledDate={(current) => current && current < fechas[0].startOf('day')}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </Col>
            </Row>
          </div>

          <div style={{ marginBottom: '25px', padding: '0 8px' }}>
            <Text strong>¿Qué datos deseas incluir?</Text>
            <Radio.Group
              onChange={e => setTipoExportacion(e.target.value)}
              value={tipoExportacion}
              style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '15px' }}
            >
              <Radio value="todos">
                <Text strong style={{ color: '#334155' }}>Toda la información</Text> del rango de fechas
              </Radio>
              <Radio value="filtrados">
                <div style={{ display: 'inline-block' }}>
                  <Text strong style={{ color: '#334155' }}>Solo resultados filtrados</Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
                    Respeta la búsqueda que tienes actualmente en pantalla.
                    {busqueda && <span style={{ color: '#0284c7', fontWeight: 'bold' }}> (Buscando: "{busqueda}")</span>}
                  </Text>
                </div>
              </Radio>
            </Radio.Group>
          </div>

          <Button
            type="primary" block size="large"
            icon={<DownloadOutlined />}
            onClick={handleDescargarExportacion}
            loading={isLoading}
            disabled={isLoading}
            style={{ background: '#0f172a', height: '50px', borderRadius: '12px', fontWeight: 'bold' }}
          >
            {isLoading ? 'Obteniendo datos...' : 'GENERAR ARCHIVO'}
          </Button>
        </Modal>

      </LayoutHome>
    </ConfigProvider>
  );
};

export default Gastos;